import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import authService from "../services/authService";
import AuthModal from "../Components/AuthModal";
// Import logo
import logo from "../assets/images/azps.png";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      // Update URL without page reload
      window.history.pushState(null, '', `/#${sectionId}`);
    }
    setOpen(false);
  };

  // Track scroll position for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check authentication
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsLoggedIn(authenticated);
      if (authenticated) {
        setUser(authService.getUser());
      }
    };
    
    checkAuth();
    window.addEventListener('storage', checkAuth);
    
    // Handle initial hash on load
    const hash = window.location.hash.slice(1);
    if (hash && ['home', 'features', 'contact'].includes(hash)) {
      setTimeout(() => scrollToSection(hash), 100);
    }
    
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  // Update active section based on scroll
  useEffect(() => {
    const handleScrollActive = () => {
      const sections = ['home', 'features', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScrollActive);
    return () => window.removeEventListener('scroll', handleScrollActive);
  }, []);

  const handleLogout = () => {
    authService.logout();
    setIsLoggedIn(false);
    setUser(null);
    scrollToSection("home");
  };

  const handleAuthSuccess = (success) => {
    if (success) {
      setIsLoggedIn(true);
      setUser(authService.getUser());
    }
    setIsAuthModalOpen(false);
  };

  const navLinks = [
    { id: "home", label: "Home" },
    { id: "features", label: "Features" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <>
      <nav className={`fixed top-4 left-4 right-4 z-50 transition-all duration-500 ${
        scrolled ? 'top-0 left-0 right-0' : ''
      }`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`bg-white/95 backdrop-blur-md border border-blue-100 shadow-xl overflow-hidden transition-all duration-500 ${
            scrolled ? 'rounded-none' : 'rounded-2xl'
          }`}>
            <div className="flex items-center justify-between py-3 md:py-4 px-4 sm:px-6">
              {/* Logo */}
              <button 
                onClick={() => scrollToSection("home")}
                className="flex items-center gap-2 group focus:outline-none"
              >
               <div className="flex items-center gap-2 group focus:outline-none relative">
  {/* Black background rectangle covering entire area */}
  <div className="absolute -inset-2 bg-black rounded-lg -z-10" />
  
  <div className="relative">
    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg blur opacity-30 group-hover:opacity-50 transition duration-300" />
  </div>
  <div className="text-left">
    <img 
        src={logo} 
        alt="Aizops Logo" 
        className="w-20 h-8 object-contain"
      />
    <div className="text-[10px] text-blue-500 font-medium tracking-wider">
      SMART HOUSEKEEPING
    </div>
  </div>
</div>
                <div className="text-left">
                  
                  
                </div>
              </button>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-1">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 relative group ${
                      activeSection === link.id
                        ? "text-blue-600"
                        : "text-slate-600 hover:text-blue-600"
                    }`}
                  >
                    {link.label}
                    {activeSection === link.id && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-blue-500 rounded-full" />
                    )}
                    <span className="absolute inset-0 rounded-lg bg-blue-50/0 group-hover:bg-blue-50/50 transition-colors duration-300" />
                  </button>
                ))}
              </div>

              {/* Desktop Actions */}
              <div className="hidden lg:flex items-center gap-3">
                {isLoggedIn ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-xs font-bold text-white">
                        {user?.name?.charAt(0) || "U"}
                      </div>
                      <span className="text-slate-700 text-sm">{user?.name?.split(' ')[0]}</span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-all duration-300"
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAuthModalOpen(true)}
                    className="px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
                  >
                    Login / Sign Up
                  </button>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden text-slate-600 text-2xl focus:outline-none w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50"
                onClick={() => setOpen(!open)}
              >
                {open ? '✕' : '☰'}
              </button>
            </div>

            {/* Mobile Menu */}
            {open && (
              <div className="lg:hidden bg-white/95 backdrop-blur-xl rounded-xl m-4 p-4 space-y-2 border border-blue-100 shadow-lg">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className={`block w-full text-left px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                      activeSection === link.id
                        ? "bg-blue-50 text-blue-600"
                        : "text-slate-600 hover:bg-blue-50/50"
                    }`}
                  >
                    {link.label}
                  </button>
                ))}
                <div className="pt-4 space-y-3 border-t border-blue-100">
                  {isLoggedIn ? (
                    <>
                      <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-blue-50">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-sm font-bold text-white">
                          {user?.name?.charAt(0) || "U"}
                        </div>
                        <span className="text-slate-700">Hi, {user?.name?.split(' ')[0]}</span>
                      </div>
                      <button
                        onClick={() => {
                          handleLogout();
                          setOpen(false);
                        }}
                        className="w-full py-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-all duration-300"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setIsAuthModalOpen(true);
                        setOpen(false);
                      }}
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium"
                    >
                      Login / Sign Up
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Add padding top to prevent content from being hidden under navbar */}
      <div className={`transition-all duration-500 ${scrolled ? 'h-16' : 'h-24'}`} />

      <AuthModal 
        isOpen={isAuthModalOpen}
        onClose={handleAuthSuccess}
        initialForm="login"
      />
    </>
  );
};

export default Navbar;